import './styles/app.css';
import { renderThemeToggle } from './components/theme-toggle';
import { setupThemeToggle } from './lib/theme';
import { currentRoute, onRouteChange, type Route } from './lib/router';
import { setRouteMeta, type RouteMeta } from './lib/meta';

// Pathname → hash hydration: if the user landed via the prerendered
// static HTML at /blog/<slug>, convert the path to the canonical
// hash route so the SPA router takes over. Runs once on boot;
// hash routes work unchanged.
{
  const m = location.pathname.match(/^\/blog\/([^/]+)\/?$/);
  if (m && !location.hash) {
    history.replaceState(null, '', `/#/blog/${m[1]}`);
  }
}

function shell(): string {
  return `
    <a href="#route-host" class="skip-link">Skip to main content</a>
    <header class="site-header">
      <div class="site-header-inner">
        <a href="#/" class="wordmark wordmark--header" aria-label="Breathe in God's World — home">
          <span class="wordmark-line-1">Breathe In</span>
          <span class="wordmark-line-2">God's World</span>
        </a>
        <div class="site-header-actions">
          <nav class="site-nav">
            <a href="#/" id="nav-blog" class="site-nav-link is-active">All entries</a>
            <a href="/lessons/" id="nav-lessons" class="site-nav-link">Lessons</a>
          </nav>
          ${renderThemeToggle()}
        </div>
      </div>
    </header>
    <main id="route-host" tabindex="-1"></main>
  `;
}

function metaForRoute(route: Route): RouteMeta {
  switch (route.name) {
    case 'blog':
      return {
        title: "Breathe in God's World — Outdoor Devotionals",
        description: "Outdoor devotionals from Breathing in God's World. Each pairs a real place with a real passage — step outside the noise and listen to the Word.",
      };
    case 'blog-entry':
      return {
        title: "Reading — Breathe in God's World",
        description: "A devotional from the series.",
      };
  }
}

async function renderRoute(route: Route): Promise<void> {
  setRouteMeta(metaForRoute(route));

  const host = document.getElementById('route-host');
  if (!host) return;

  host.innerHTML = '<div class="loading-state">Loading…</div>';

  try {
    if (route.name === 'blog') {
      const { teardownReadingProgress } = await import('./components/blog-entry');
      teardownReadingProgress();
      const [{ renderBlogListing, renderBlogListingError }, { fetchDevotionalIndex }] = await Promise.all([
        import('./components/blog-listing'),
        import('./lib/api'),
      ]);
      const r = await fetchDevotionalIndex();
      host.innerHTML = r.error
        ? renderBlogListingError(r.error)
        : `<div class="blog-container">${renderBlogListing(r.data)}</div>`;
    } else if (route.name === 'blog-entry') {
      const [
        { renderBlogEntry, renderBlogNotFound, setupReadingProgress },
        { fetchDevotionalEntry },
      ] = await Promise.all([
        import('./components/blog-entry'),
        import('./lib/api'),
      ]);
      const r = await fetchDevotionalEntry(route.slug);
      if (r.error) {
        host.innerHTML = renderBlogNotFound(r.error);
      } else if (!r.data) {
        host.innerHTML = renderBlogNotFound();
      } else {
        host.innerHTML = `<div class="blog-container">${renderBlogEntry(r.data)}</div>`;
        setupReadingProgress();
        setRouteMeta({
          title: `${r.data.title} | Breathe in God's World`,
          description: r.data.description ?? r.data.excerpt,
          image: `/data/devotionals/imagery/entry-${r.data.slug}.webp`,
        });
      }
    }
  } catch (e) {
    host.innerHTML = `<div class="loading-state">Couldn't load: ${(e as Error).message}</div>`;
  }

  window.scrollTo(0, 0);
}

const app = document.getElementById('app');
if (app) {
  app.innerHTML = shell();
  setupThemeToggle();
  onRouteChange((r) => { void renderRoute(r); });
  void renderRoute(currentRoute());
}

console.log('[BGW] boot');
